import React, { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  WorkspaceView,
  createAllView,
  createMyView,
  createGroupView,
} from '../../../types/workspaceView';
import { SupportGroupStats } from '../../../types/supportGroup';
import { supportGroupService } from '../../../services/supportGroupService';
import { WorkspaceThemeClasses } from './workspaceTheme';

interface WorkspaceViewSelectorProps {
  theme: WorkspaceThemeClasses;
  activeView: WorkspaceView;
  onViewChange: (view: WorkspaceView) => void;
}

type OptionKey = 'all' | 'mine' | `group-${string}`;

const optionKeyFor = (view: WorkspaceView): OptionKey => {
  if (view.type === 'group' && view.supportGroupId)
  {
    return `group-${view.supportGroupId}`;
  }

  return view.type === 'mine' ? 'mine' : 'all';
};

export const WorkspaceViewSelector: React.FC<WorkspaceViewSelectorProps> = ({
  theme,
  activeView,
  onViewChange,
}) => {
  const [groups, setGroups] = useState<SupportGroupStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGroups = async () => {
      try
      {
        const data = await supportGroupService.getStats();
        setGroups(data.filter((group) => group.isActive));
      }
      catch
      {
        setGroups([]);
      }
      finally
      {
        setLoading(false);
      }
    };

    void loadGroups();
  }, []);

  const handleSelect = (key: OptionKey) => {
    if (key === 'all')
    {
      onViewChange(createAllView());
      return;
    }

    if (key === 'mine')
    {
      onViewChange(createMyView());
      return;
    }

    const supportGroupId = key.slice('group-'.length);
    const group = groups.find((item) => item.supportGroupId === supportGroupId);
    if (group)
    {
      onViewChange(createGroupView(group.supportGroupId, group.name));
    }
  };

  const activeKey = optionKeyFor(activeView);

  return (
    <div className="mt-2.5">
      <label className={`block text-[10.5px] font-bold uppercase tracking-wide mb-1 ${theme.faint}`}>
        Visualização
      </label>
      <Listbox value={activeKey} onChange={handleSelect} disabled={loading}>
        <div className="relative">
          <Listbox.Button
            className={`relative w-full cursor-pointer rounded-md border px-2.5 py-1.5 pr-8 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${theme.input}`}
          >
            <span className={`block truncate font-medium ${theme.text}`}>{activeView.label}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className={`h-4 w-4 ${theme.faint}`} aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={`absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border py-1 text-xs shadow-lg focus:outline-none ${theme.border} ${theme.surface}`}
            >
              <Listbox.Option value="all" as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-pointer select-none px-3 py-2 ${
                      active ? theme.selectedRow : ''
                    }`}
                  >
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                      Todas as conversas
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                )}
              </Listbox.Option>

              <Listbox.Option value="mine" as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-pointer select-none px-3 py-2 ${
                      active ? theme.selectedRow : ''
                    }`}
                  >
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                      Minhas conversas
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                )}
              </Listbox.Option>

              {groups.length > 0 && (
                <>
                  <div className={`my-1 border-t ${theme.border}`} />
                  <p className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-wide ${theme.faint}`}>
                    Grupos de atendimento
                  </p>

                  {groups.map((group) => (
                    <Listbox.Option
                      key={group.supportGroupId}
                      value={`group-${group.supportGroupId}` as OptionKey}
                      as={Fragment}
                    >
                      {({ active, selected }) => (
                        <li
                          className={`relative cursor-pointer select-none px-3 py-2 ${
                            active ? theme.selectedRow : ''
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: group.color || '#9ca3af' }}
                              aria-hidden="true"
                            />
                            <span className={`flex-1 truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                              {group.name}
                            </span>
                            <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${theme.badge}`}>
                              {group.openConversationCount}
                            </span>
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-7 flex items-center text-blue-600">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </li>
                      )}
                    </Listbox.Option>
                  ))}
                </>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
